'use client';

import { useMemo, useCallback } from 'react';
import { useSavedVolume } from 'src/hooks/savedVolume/useSavedVolume';
import { useCodeSetSelectorData } from './useCodeSetSelectorData';
import type { CodeSetItem } from '../types';
import type { AccountFolder } from 'src/api/redux/accountFolders';

const sortByLabel = (a: any, b: any) =>
  (a.label || '').localeCompare(b.label || '');

/**
 * The saved volumes (cohorts, code sets, AI searches) a user sees, split into
 * personal/company and folders, plus the volume applied at `location`. Shared by
 * the Discovery saved-volumes dropdown and the Manage Code Sets drawer so both
 * always list the same items.
 */
export const useSavedVolumePartitions = (location: string) => {
  const { appliedSavedVolume, setSavedVolume } = useSavedVolume(location);

  // Poll the relevant endpoint while a processing volume is selected.
  const isCohortProcessing =
    appliedSavedVolume?.volumeType === 'cohort' &&
    appliedSavedVolume?.status === 'PROCESSING';
  const isAdvancedSearchProcessing =
    appliedSavedVolume?.volumeType === 'advancedSearch' &&
    appliedSavedVolume?.status === 'PROCESSING';

  // Shared data layer. Discovery keeps deactivated cohorts (so they can be
  // reactivated) and scope-matches folder items like the Lists column selector.
  const {
    ungroupedItems,
    getFolderItems,
    isLoading,
    error,
    refetch,
    raw: {
      cohortsData,
      filterSetsData: allCodeSets,
      advancedSearchesData,
      accountFoldersData,
      mutateAccountFolders,
    },
  } = useCodeSetSelectorData({
    deactivatedPolicy: 'include',
    includeAdvancedSearches: true,
    cohortsQueryOptions: {
      pollingInterval: isCohortProcessing ? 60_000 : 0,
      skipPollingIfUnfocused: true,
    },
    advancedSearchesQueryOptions: {
      pollingInterval: isAdvancedSearchProcessing ? 60_000 : 0,
      skipPollingIfUnfocused: true,
    },
  });

  // Map a normalized item to Discovery's volume shape (adds `volumeType`).
  const toVolumeItem = useCallback((item: CodeSetItem) => {
    if (item.kind === 'cohort') {
      return { ...item.raw, status: item.status, volumeType: 'cohort' as const };
    }
    if (item.kind === 'advancedSearch') {
      return {
        ...item.raw,
        volumeType: 'advancedSearch' as const,
        scope: item.raw.scope?.toLowerCase() ?? 'personal',
      };
    }
    return {
      id: item.raw.id,
      label: item.raw.label,
      type: item.raw.type || 'CODE',
      scope: item.raw.scope,
      owner: item.raw.owner,
      filters: item.raw.filters || [],
      created_at: item.raw.created_at || new Date().toISOString(),
      status: item.raw.status,
      volumeType: 'filterSet' as const,
    };
  }, []);

  // Partition ungrouped items + folders into personal/company for rendering.
  const { personalVolumes, companyVolumes, personalFolders, companyFolders } =
    useMemo(() => {
      const result = {
        personalVolumes: [] as any[],
        companyVolumes: [] as any[],
        personalFolders: [] as AccountFolder[],
        companyFolders: [] as AccountFolder[],
      };

      accountFoldersData?.results?.forEach((folder: AccountFolder) => {
        if (folder.scope === 'PERSONAL') {
          result.personalFolders.push(folder);
        } else if (folder.scope === 'COMPANY') {
          result.companyFolders.push(folder);
        }
      });

      ungroupedItems.forEach((item) => {
        const volume = toVolumeItem(item);
        // Preserve original routing: cohorts fall back to company, filter sets
        // and advanced searches to personal, when the scope is unrecognized.
        if (item.kind === 'cohort') {
          if (item.scope?.toLowerCase() === 'personal') {
            result.personalVolumes.push(volume);
          } else {
            result.companyVolumes.push(volume);
          }
        } else if (item.scope?.toLowerCase() === 'company') {
          result.companyVolumes.push(volume);
        } else {
          result.personalVolumes.push(volume);
        }
      });

      return {
        personalVolumes: result.personalVolumes.sort(sortByLabel),
        companyVolumes: result.companyVolumes.sort(sortByLabel),
        personalFolders: result.personalFolders.sort(sortByLabel),
        companyFolders: result.companyFolders.sort(sortByLabel),
      };
    }, [ungroupedItems, accountFoldersData, toVolumeItem]);

  const getFolderVolumes = useCallback(
    (folder: AccountFolder) =>
      getFolderItems(folder).map(toVolumeItem).sort(sortByLabel),
    [getFolderItems, toVolumeItem]
  );

  // Current code set for data coverage info. Matches on type too, since
  // cohort, code set and AI search ids can collide.
  const currentCodeSet = useMemo(() => {
    if (appliedSavedVolume?.volumeType !== 'filterSet') return undefined;
    return allCodeSets?.results?.find(
      (codeSet: any) => codeSet.id === appliedSavedVolume.id
    );
  }, [allCodeSets, appliedSavedVolume]);

  /**
   * After a delete: refetch folders, since the delete mutations don't
   * invalidate them and folder items fall back to the folder payload, which
   * still lists the deleted item. Then clear the applied volume when it is the
   * one just deleted, matching on type too since cohort, code set and AI search
   * ids can collide.
   */
  const handleVolumeDeleted = useCallback(
    (deleted: { id: number; volumeType?: string } | null) => {
      mutateAccountFolders();
      if (
        appliedSavedVolume &&
        deleted &&
        appliedSavedVolume.id === deleted.id &&
        appliedSavedVolume.volumeType === deleted.volumeType
      ) {
        setSavedVolume(null);
      }
    },
    [appliedSavedVolume, setSavedVolume, mutateAccountFolders]
  );

  /** Keep the applied volume's label current when that AI search is renamed. */
  const renameAppliedAdvancedSearch = useCallback(
    (id: number, newLabel: string) => {
      if (
        appliedSavedVolume?.id === id &&
        appliedSavedVolume.volumeType === 'advancedSearch'
      ) {
        setSavedVolume({ ...appliedSavedVolume, label: newLabel });
      }
    },
    [appliedSavedVolume, setSavedVolume]
  );

  const currentProviderCohort =
    appliedSavedVolume?.volumeType === 'cohort'
      ? cohortsData?.results?.find(
          (cohort: any) => cohort.id === appliedSavedVolume.id
        )
      : undefined;

  return {
    personalVolumes,
    companyVolumes,
    personalFolders,
    companyFolders,
    isLoading,
    error,
    refetch,
    allCodeSets,
    cohortsData,
    advancedSearchesData,
    accountFoldersData,
    mutateAccountFolders,
    getFolderVolumes,
    currentCodeSet,
    currentProviderCohort,
    appliedSavedVolume,
    setSavedVolume,
    handleVolumeDeleted,
    renameAppliedAdvancedSearch,
  };
};
